import type React from "react";
import { ArrowLeftRight } from "lucide-react";
import { Button } from "@planta-v2/pds-ui/button";
import { Skeleton } from "@planta-v2/pds-ui/skeleton";
import {
  UnderlineTabs,
  UnderlineTabsList,
  UnderlineTabsTrigger,
  UnderlineTabsContent,
} from "@planta-v2/pds-ui/underline-tabs";
import { LocalizedTextUtils } from "@shared/utils/localized-text.utils";
import { i18n } from "@frontend/i18n";
import { useEventThemeFromConfig } from "@/shared/presentation/hooks/useEventThemeFromConfig";
import { ProfileHeader } from "../components/layout/ProfileHeader";
import { ProfileEditForm } from "../components/ProfileEditForm";
import { PreferencesForm } from "../components/PreferencesForm";
import { FutureEventsSection } from "../components/FutureEventsSection";
import { DirectoryExportSection } from "../components/DirectoryExportSection";
import { useProfileEditController } from "../controllers/useProfileEditController";
import type {
  ProfileEditState,
  ProfileEditActions,
} from "../controllers/useProfileEditController";

interface TabContentPropsType {
  readonly state: ProfileEditState;
  readonly actions: ProfileEditActions;
}

function ProfileTabContent({
  state,
  actions,
  onOpenPhotoPage,
}: TabContentPropsType & {
  readonly onOpenPhotoPage: () => void;
}): React.JSX.Element {
  if (state.isLoading) {
    return (
      <div className="flex flex-col gap-4 px-4 py-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (state.error !== null) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <p className="text-sm text-center text-destructive">
          {i18n.t("user.profile.form.errorLoading")}
        </p>
      </div>
    );
  }

  if (state.participant === undefined) {
    return <></>;
  }

  return (
    <>
      {state.saveError !== null && (
        <div className="px-4 pt-4">
          <p className="text-center text-sm text-destructive">
            {state.saveError.message !== ""
              ? state.saveError.message
              : i18n.t("user.profile.form.errorSaving")}
          </p>
        </div>
      )}
      <ProfileEditForm
        participant={state.participant}
        lockedFields={state.lockedFields}
        instructions={state.instructions}
        externalEdit={state.externalEdit}
        isSaving={state.isSaving}
        isUploadingPhoto={state.isUploadingPhoto}
        isRemovingPhoto={state.isRemovingPhoto}
        canNavigateToDirectory={state.canNavigateToDirectory}
        onSave={actions.onSave}
        onUploadPhoto={actions.onUploadPhoto}
        onRemovePhoto={actions.onRemovePhoto}
        onOpenPhotoPage={onOpenPhotoPage}
        onNavigateToDirectory={actions.onNavigateToDirectory}
      />
    </>
  );
}

function PreferencesTabContent({
  state,
  actions,
}: TabContentPropsType): React.JSX.Element {
  return (
    <>
      {state.preferencesError !== null && !state.isLoadingPreferences && (
        <div className="px-4 pt-4">
          <p className="text-center text-sm text-destructive">
            {i18n.t("user.profile.preferences.errorLoading")}
          </p>
        </div>
      )}
      {state.saveError !== null && !state.isLoadingPreferences && (
        <div className="px-4 pt-4">
          <p className="text-center text-sm text-destructive">
            {state.saveError.message !== ""
              ? state.saveError.message
              : i18n.t("user.profile.preferences.errorSaving")}
          </p>
        </div>
      )}
      <PreferencesForm
        initialData={state.preferences}
        onFieldChange={actions.onPreferencesFieldChange}
        isLoading={state.isLoadingPreferences}
        beforeActions={
          state.event?.isolated === true ? (
            <FutureEventsSection
              participantIsolated={state.participant?.isolated ?? true}
              isOptingIn={state.isOptingIn}
              onOptIn={(): void => {
                void actions.onOptInRemember();
              }}
            />
          ) : undefined
        }
      />
    </>
  );
}

export function ProfileManagementPage(): React.JSX.Element {
  const { state, actions, nav } = useProfileEditController();

  useEventThemeFromConfig(state.eventIdPublic);

  const handleTabChange = (value: string): void => {
    if (value === "profile" || value === "preferences") {
      actions.setActiveTab(value);
    }
  };

  const handleDirectoryClick = (): void => {
    nav.toDirectory();
  };

  const handleAccountClick = (): void => {
    if (state.userIdPublic !== null) {
      nav.toUserAccount(state.userIdPublic);
    }
  };

  const handleSwitchEventClick = (): void => {
    if (state.userIdPublic !== null) {
      nav.toUserAccountEvents(state.userIdPublic);
    }
  };

  const eventName =
    state.event !== undefined
      ? LocalizedTextUtils.resolve(state.event.name, i18n.getLocale())
      : "";

  return (
    <div
      className="flex h-dvh flex-col bg-surface"
      data-testid="profile-management-page"
    >
      <ProfileHeader
        onDirectoryClick={handleDirectoryClick}
        onAccountClick={handleAccountClick}
        showAccountButton={
          !state.isLoading && !(state.participant?.isolated ?? false)
        }
      />

      <div className="flex gap-3 items-start p-4 bg-surface shadow-xs">
        <div className="flex-1 min-w-0">
          <h1 className="page-title">{i18n.t("user.profile.title")}</h1>
          {eventName !== "" && (
            <p className="truncate text-pds-2xs text-muted-foreground">
              {eventName}
            </p>
          )}
        </div>
        {!state.isEventSubdomain && !(state.participant?.isolated ?? false) && (
          <Button
            variant="outline"
            size="icon"
            aria-label={i18n.t("user.profile.header.switchEvent")}
            onClick={handleSwitchEventClick}
          >
            <ArrowLeftRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Tab bar + content */}
      <UnderlineTabs
        value={state.activeTab}
        onValueChange={handleTabChange}
        className="flex flex-1 flex-col min-h-0"
      >
        <UnderlineTabsList className="bg-surface px-4">
          <UnderlineTabsTrigger
            value="profile"
            data-testid="profile-tab-trigger"
          >
            {i18n.t("user.profile.tabs.profile")}
          </UnderlineTabsTrigger>
          <UnderlineTabsTrigger
            value="preferences"
            data-testid="preferences-tab-trigger"
          >
            {i18n.t("user.profile.tabs.preferences")}
          </UnderlineTabsTrigger>
        </UnderlineTabsList>

        <UnderlineTabsContent
          value="profile"
          className="flex-1 overflow-y-auto min-h-0"
          data-testid="profile-tab-content"
        >
          <ProfileTabContent
            state={state}
            actions={actions}
            onOpenPhotoPage={nav.toProfilePhoto}
          />
        </UnderlineTabsContent>

        <UnderlineTabsContent
          value="preferences"
          className="flex-1 overflow-y-auto min-h-0"
          data-testid="preferences-tab-content"
        >
          <DirectoryExportSection />
          <PreferencesTabContent state={state} actions={actions} />
        </UnderlineTabsContent>
      </UnderlineTabs>
    </div>
  );
}
